import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { INTERNAL_PROTECTED_ROLE_NAMES } from 'src/constants/internal-access/internalRoles'
import type {
  InternalAccessRole,
  InternalAccessUser,
  InternalRoleOption,
} from 'src/interfaces/internalAccess'
import { isProtectedInternalRole } from './internalRoles'

/** Name when the directory supplied one, else the email — nothing invented. */
export const buildInternalUserName = (user: InternalAccessUser): string => {
  const fullName = [user.firstName, user.lastName]
    .filter((namePart) => {
      return Boolean(namePart)
    })
    .join(' ')

  return fullName || user.email
}

/** In-memory: the endpoint has no query params and returns every user at once. */
export const filterInternalUsers = (
  users: InternalAccessUser[],
  query: string,
): InternalAccessUser[] => {
  const normalizedQuery = query.trim().toLowerCase()

  if (!normalizedQuery) {
    return users
  }

  return users.filter((user) => {
    const searchable = `${buildInternalUserName(user)} ${user.email}`.toLowerCase()

    return searchable.includes(normalizedQuery)
  })
}

/**
 * `super_admin` is granted by a deliberate database action, never from this dropdown. The backend
 * also refuses (403) a role whose effective permissions the editor does not hold in full, so those
 * roles are left out too — an `allPermissions` role's effective set is every `InternalPermission`.
 * `grantable` is the editor's own permission list.
 */
export const buildAssignableRoleOptions = (
  roles: InternalAccessRole[],
  grantable: string[],
): InternalRoleOption[] => {
  return roles
    .filter((role) => {
      if (isProtectedInternalRole(role)) {
        return false
      }

      const effectivePermissions: string[] = role.allPermissions
        ? Object.values(InternalPermission)
        : role.permissions

      return effectivePermissions.every((permission) => {
        return grantable.includes(permission)
      })
    })
    .map((role) => {
      return { label: role.name, value: role.id }
    })
}

/**
 * The select only holds assignable roles, so a protected or unknown current role starts blank —
 * the Role row above the select still shows it.
 */
export const resolveInitialRoleId = (
  user: InternalAccessUser,
  roles: InternalAccessRole[],
): string => {
  if (!user.roleId) {
    return ''
  }

  const currentRole = roles.find((role) => {
    return role.id === user.roleId
  })

  if (!currentRole || isProtectedInternalRole(currentRole)) {
    return ''
  }

  return currentRole.id
}

export const countUsersByRole = (users: InternalAccessUser[]): Record<string, number> => {
  return users.reduce<Record<string, number>>((counts, user) => {
    if (!user.roleId) {
      return counts
    }

    counts[user.roleId] = (counts[user.roleId] ?? 0) + 1

    return counts
  }, {})
}

const isProtectedRoleName = (roleName: string | null | undefined): boolean => {
  return Boolean(roleName) && INTERNAL_PROTECTED_ROLE_NAMES.includes(roleName as string)
}

/**
 * Only a super admin may change or disable another super admin. The backend does not enforce this
 * yet (it only refuses self-changes and removing the LAST enabled super admin), so this lock is
 * the only guard until it does.
 */
export const isProtectedUserLockedFor = (
  user: InternalAccessUser,
  roles: InternalAccessRole[],
  viewerRoleName: string | null,
): boolean => {
  const userRole = roles.find((role) => {
    return role.id === user.roleId
  })
  const isUserProtected = userRole
    ? isProtectedInternalRole(userRole)
    : isProtectedRoleName(user.roleName)

  return isUserProtected && !isProtectedRoleName(viewerRoleName)
}
